import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { fetchBentoAdmin, saveBento } from '../../services/site';
import { uploadMediaFile } from '../../services/media';

const CELLS = [
  { key: 'mission', label: 'Notre mission (grande carte)' },
  { key: 'don', label: 'Faire un don' },
  { key: 'galerie', label: 'Galerie' },
  { key: 'carte', label: "Zones d'intervention" },
  { key: 'boutique', label: 'Boutique' },
  { key: 'blog', label: 'Blog & Tracts' },
  { key: 'equipe', label: "L'équipe" },
  { key: 'contact', label: 'Contact' },
];

const BentoSettings = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({});

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchBentoAdmin(token);
        setForm(data || {});
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!user || user.user_type !== 'fondateur') {
    return <div className="p-6 text-sm text-red-500">Accès réservé au fondateur.</div>;
  }

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onUpload = async (key, file) => {
    if (!file) return;
    try {
      setUploading(key); setError('');
      const up = await uploadMediaFile(file, token);
      if (up?.url) set(key, up.url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading('');
    }
  };

  const onSave = async () => {
    try {
      setSaving(true); setError(''); setSuccess('');
      await saveBento(form, token);
      setSuccess('✅ Section « Explorer PADAID » mise à jour. Elle s\'affiche sur la page d\'accueil.');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="bento-settings-page">
      <Card>
        <CardHeader>
          <CardTitle>Section « Explorer PADAID » (accueil)</CardTitle>
          <CardDescription>Modifiez le titre, les textes et les photos de la grille de la page d'accueil.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && <Alert><AlertDescription className="text-red-600" data-testid="bento-error">{error}</AlertDescription></Alert>}
          {success && <Alert><AlertDescription className="text-green-600" data-testid="bento-success">{success}</AlertDescription></Alert>}

          {/* En-tête de section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1">Sur-titre</label>
              <Input data-testid="bento-eyebrow" value={form.eyebrow || ''} onChange={(e) => set('eyebrow', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm mb-1">Titre principal</label>
              <Input data-testid="bento-title" value={form.title || ''} onChange={(e) => set('title', e.target.value)} />
            </div>
          </div>

          {/* Photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'hero_image', label: 'Photo grande carte (mission)' },
              { key: 'galerie_image', label: 'Photo Galerie' },
            ].map(({ key, label }) => (
              <div key={key} className="rounded-lg border border-border p-4">
                <label className="block text-sm mb-2 font-medium">{label}</label>
                {form[key] && <img src={form[key]} alt="aperçu" className="h-24 w-full rounded-md object-cover border border-border mb-2" />}
                <input type="file" accept="image/*" data-testid={`bento-upload-${key}`} onChange={(e) => onUpload(key, e.target.files?.[0])} className="text-sm" />
                {uploading === key && <p className="text-xs text-muted-foreground mt-1">Téléversement…</p>}
              </div>
            ))}
          </div>

          {/* Textes des cellules */}
          <div className="space-y-4">
            {CELLS.map(({ key, label }) => (
              <div key={key} className="rounded-lg border border-border p-4">
                <p className="text-sm font-semibold mb-3">{label}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1 text-muted-foreground">Titre</label>
                    <Input data-testid={`bento-${key}-title`} value={form[`${key}_title`] || ''} onChange={(e) => set(`${key}_title`, e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs mb-1 text-muted-foreground">Description</label>
                    <Input data-testid={`bento-${key}-desc`} value={form[`${key}_desc`] || ''} onChange={(e) => set(`${key}_desc`, e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button data-testid="bento-save-btn" onClick={onSave} disabled={saving}>{saving ? 'Enregistrement…' : 'Sauvegarder'}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BentoSettings;
